import { useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserRound, KeyRound, Building2, Plus, Trash2, Users as UsersIcon, ShieldCheck, Eye, Copy, Wrench } from "lucide-react";

export default function AccountSettings({ open, onOpenChange, user, onUserUpdated }) {
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "ADMIN").toUpperCase() === "ADMIN";
  const { companies, company, setCompany, refresh: refreshCompanies } = useCompany();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [newCompanyName, setNewCompanyName] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);

  // Users management (admin only)
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "VIEWER", companies: [] });
  const [savingUser, setSavingUser] = useState(false);

  const loadUsers = async () => {
    if (!isAdmin) return;
    try { const r = await api.get("/users"); setUsers(r.data); } catch {}
  };

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setCurrent(""); setNext(""); setConfirm("");
      setNewCompanyName("");
      setNewUser({ email: "", name: "", password: "", role: "VIEWER", companies: [] });
      refreshCompanies?.();
      loadUsers();
    }
  }, [open, user, isAdmin]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.email.trim() || !newUser.password || newUser.password.length < 6) {
      toast.error("Email + password (min 6 chars) required");
      return;
    }
    setSavingUser(true);
    try {
      await api.post("/users", newUser);
      toast.success(`Created ${newUser.email}`);
      setNewUser({ email: "", name: "", password: "", role: "VIEWER", companies: [] });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally { setSavingUser(false); }
  };

  const patchUser = async (id, body) => {
    try { await api.patch(`/users/${id}`, body); loadUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); loadUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const toggleUserCompany = (u, c) => {
    const next = new Set(u.companies || []);
    if (next.has(c)) next.delete(c); else next.add(c);
    patchUser(u.id, { companies: Array.from(next) });
  };

  const [dedupeLoading, setDedupeLoading] = useState(false);
  const runDedupe = async () => {
    if (!window.confirm("Scan bookings for duplicates and merge them? Duplicates are those sharing property + check-in + check-out + guest.")) return;
    setDedupeLoading(true);
    try {
      const dry = await api.post("/bookings/dedupe?dry_run=true");
      const n = dry.data.duplicate_groups?.length || 0;
      if (n === 0) { toast.success("No duplicates found"); return; }
      if (!window.confirm(`Found ${n} duplicate group${n === 1 ? "" : "s"}. Merge now?`)) return;
      const res = await api.post("/bookings/dedupe");
      toast.success(`Merged ${res.data.merged || 0} duplicate rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dedupe failed");
    } finally {
      setDedupeLoading(false);
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    const trimmed = newCompanyName.trim();
    if (!trimmed) return;
    setCreatingCompany(true);
    try {
      const r = await api.post("/companies", { name: trimmed });
      toast.success(`Added ${r.data.name}`);
      setNewCompanyName("");
      await refreshCompanies?.();
      setCompany(r.data.name);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add company");
    } finally {
      setCreatingCompany(false);
    }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"? This only removes the label — bookings must be reassigned or archived first.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success(`Removed ${c.name}`);
      await refreshCompanies?.();
      if (company === c.name && companies.length > 1) {
        const first = companies.find((x) => x.name !== c.name);
        if (first) setCompany(first.name);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const r = await api.patch("/auth/profile", { name, email });
      toast.success("Profile updated");
      onUserUpdated?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.patch("/auth/password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Account Settings</DialogTitle>
          <DialogDescription>Manage your profile, password{isAdmin ? ", companies and users" : ""}.</DialogDescription>
        </DialogHeader>

        <form onSubmit={saveProfile} className="space-y-4 border-b border-slate-200 pb-6" data-testid="profile-form">
          <div className="flex items-center gap-2">
            <UserRound className="w-4 h-4 text-blue-900" />
            <span className="label-eyebrow">Profile</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-sm border-slate-300"
                data-testid="account-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Email (login)</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm border-slate-300"
                data-testid="account-email"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={savingProfile}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white"
            data-testid="account-save-profile"
          >
            {savingProfile ? "Saving…" : "Save profile"}
          </Button>
        </form>

        <form onSubmit={savePassword} className="space-y-4 pt-2" data-testid="password-form">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blue-900" />
            <span className="label-eyebrow">Change password</span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Current password</Label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                className="rounded-sm border-slate-300"
                data-testid="account-current-password"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">New password</Label>
                <Input
                  type="password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-sm border-slate-300"
                  data-testid="account-new-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Confirm new</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="rounded-sm border-slate-300"
                  data-testid="account-confirm-password"
                />
              </div>
            </div>
          </div>
          <Button
            type="submit"
            disabled={savingPw}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white"
            data-testid="account-save-password"
          >
            {savingPw ? "Saving…" : "Change password"}
          </Button>
        </form>

        {/* Data maintenance (admin only) */}
        {isAdmin && (
          <div className="pt-6 border-t border-slate-200 space-y-3" data-testid="maintenance-panel">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-blue-900" />
              <span className="label-eyebrow">Data maintenance</span>
            </div>
            <p className="text-xs text-slate-500">
              Admin-only utilities. Duplicates are rows sharing the same property, check-in, check-out and guest.
            </p>
            <Button
              type="button"
              onClick={runDedupe}
              disabled={dedupeLoading}
              variant="outline"
              className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="settings-dedupe"
            >
              <Copy className="w-4 h-4 mr-2" />
              {dedupeLoading ? "Scanning…" : "Clean duplicate bookings"}
            </Button>
          </div>
        )}

        {/* Companies management (admin only) */}
        {isAdmin && (
        <div className="pt-6 border-t border-slate-200 space-y-4" data-testid="companies-panel">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-900" />
            <span className="label-eyebrow">Companies</span>
          </div>
          <p className="text-xs text-slate-500">
            Add companies to isolate bookings, dashboards, calendar, reports and payments per business.
          </p>

          <form onSubmit={createCompany} className="flex gap-2" data-testid="create-company-form">
            <Input
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              placeholder="e.g. Acme Ltd"
              className="rounded-sm border-slate-300"
              data-testid="new-company-input"
            />
            <Button
              type="submit"
              disabled={creatingCompany || !newCompanyName.trim()}
              className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white shrink-0"
              data-testid="create-company-submit"
            >
              <Plus className="w-4 h-4 mr-1" /> {creatingCompany ? "Adding…" : "Add"}
            </Button>
          </form>

          <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
            {companies.length === 0 && (
              <div className="p-3 text-xs text-slate-500 text-center">No companies yet.</div>
            )}
            {companies.map((c) => {
              const isActive = c.name === company;
              const isProtected = c.name === "Flexistay Luton Limited";
              return (
                <div
                  key={c.id || c.name}
                  className="flex items-center justify-between px-3 py-2.5 gap-3"
                  data-testid={`company-row-${c.name}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    {isActive && (
                      <div className="text-[10px] label-eyebrow text-blue-900">Active</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isActive && (
                      <button
                        onClick={() => setCompany(c.name)}
                        className="text-[10px] uppercase tracking-wider text-blue-900 hover:underline"
                        data-testid={`switch-company-${c.name}`}
                      >
                        Switch
                      </button>
                    )}
                    {!isProtected && (
                      <button
                        onClick={() => deleteCompany(c)}
                        className="text-slate-400 hover:text-rose-700 transition-colors"
                        title={`Delete ${c.name}`}
                        data-testid={`delete-company-${c.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Users & roles (admin only) */}
        {isAdmin && (
        <div className="pt-6 border-t border-slate-200 space-y-4" data-testid="users-panel">
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-blue-900" />
            <span className="label-eyebrow">Users & roles</span>
          </div>
          <p className="text-xs text-slate-500">
            Admins see everything and can manage data. Viewers only see the companies you assign and cannot dedupe, archive, purge or delete records.
          </p>

          <form onSubmit={createUser} className="grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid="create-user-form">
            <Input value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} placeholder="email@example.com" className="rounded-sm border-slate-300 sm:col-span-2" data-testid="new-user-email" />
            <Input value={newUser.name} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} placeholder="Name" className="rounded-sm border-slate-300 sm:col-span-1" data-testid="new-user-name" />
            <Input type="password" value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} placeholder="Password (min 6)" className="rounded-sm border-slate-300 sm:col-span-2" data-testid="new-user-password" />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser((u) => ({ ...u, role: e.target.value }))}
              className="h-10 px-2 rounded-sm border border-slate-300 text-sm bg-white sm:col-span-1"
              data-testid="new-user-role"
            >
              <option value="VIEWER">Viewer</option>
              <option value="ADMIN">Admin</option>
            </select>
            <Button type="submit" disabled={savingUser} className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white sm:col-span-6" data-testid="create-user-submit">
              <Plus className="w-4 h-4 mr-1" /> {savingUser ? "Adding…" : "Add user"}
            </Button>
          </form>

          <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
            {users.length === 0 && (
              <div className="p-3 text-xs text-slate-500 text-center">No users yet.</div>
            )}
            {users.map((u) => {
              const isSelf = u.id === authUser?.id;
              const isAdminRow = (u.role || "ADMIN").toUpperCase() === "ADMIN";
              return (
                <div key={u.id} className="p-3 space-y-2" data-testid={`user-row-${u.email}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {u.email}
                        {isSelf && <span className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-sm">You</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{u.name || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={u.role || "ADMIN"}
                        onChange={(e) => patchUser(u.id, { role: e.target.value })}
                        disabled={isSelf}
                        className="h-8 px-2 rounded-sm border border-slate-300 text-xs bg-white"
                        data-testid={`user-role-${u.email}`}
                      >
                        <option value="VIEWER">Viewer</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                      {isAdminRow ? <ShieldCheck className="w-4 h-4 text-blue-900" /> : <Eye className="w-4 h-4 text-slate-500" />}
                      {!isSelf && (
                        <button onClick={() => removeUser(u)} className="text-slate-400 hover:text-rose-700" data-testid={`user-delete-${u.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {!isAdminRow && (
                    <div className="flex flex-wrap gap-1.5" data-testid={`user-companies-${u.email}`}>
                      {companies.map((c) => {
                        const on = (u.companies || []).includes(c.name);
                        return (
                          <button
                            key={c.id || c.name}
                            onClick={() => toggleUserCompany(u, c.name)}
                            className={`text-[10px] px-2 py-0.5 rounded-sm border transition-colors ${on ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:border-blue-900"}`}
                            data-testid={`user-company-toggle-${u.email}-${c.name}`}
                          >
                            {c.name}
                          </button>
                        );
                      })}
                      {(u.companies || []).length === 0 && (
                        <span className="text-[10px] text-rose-600">No companies assigned — user cannot see any data</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
